import React, { useCallback } from 'react';
import { type Href, router } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet } from 'react-native';

import { IconLink, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const DefaultHeaderLeft = (props: DefaultHeaderLeftProps) => {
  const { fallbackHref = '/', onPress, accessibilityLabel } = props;

  const { formatMessage } = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress();

      return;
    }

    if (router.canGoBack()) {
      router.back();

      return;
    }

    if (router.canDismiss()) {
      router.dismissTo(fallbackHref);

      return;
    }

    router.navigate(fallbackHref);
  }, [fallbackHref, onPress]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <IconLink
      width={40}
      height={40}
      name="IconArrowLeft"
      palette="muted"
      accessibilityLabel={accessibilityLabel ?? formatMessage(messages.goBack)}
      onPress={handlePress}
      style={styles.iconLink}
    />
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const defaultHeaderLeft = () => <DefaultHeaderLeft />;

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  goBack: {
    defaultMessage: 'Go back',
    description: 'Default Header Left | Back button accessibility label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  iconLink: {
    marginLeft: Platform.select({
      web: theme.spacing['4'],
      default: 0,
    }),
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type DefaultHeaderLeftProps = {
  fallbackHref?: Href;
  onPress?: () => void;
  accessibilityLabel?: string;
};
